import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { request } from "../../utils/api";
import {
  CloseIcon,
  GraduationCapIcon,
  HeartPulseIcon,
  LibraryIcon,
  ReceiptIcon,
  SearchIcon,
  UsersIcon,
} from "../../Icons";
import ModuleTabPanel from "../../ui/ModuleTabPanel";
import {
  FILTER_SELECT,
  PAGE_HEADER_ROW,
  PAGE_SHELL,
  PAGE_SUBTITLE,
  PAGE_TITLE,
  SEARCH_INPUT,
} from "../../ui/pageChrome";
import { BRAND_THEME } from "../../ui/brandTheme";
import { useUrlTab } from "../../hooks/useUrlTab";
import {
  TRAINING_CATEGORIES,
  TRAINING_SORT_OPTIONS,
} from "./trainingCatalog";
import TrainingVideoCard from "./TrainingVideoCard";
import TrainingVideoPlayerModal from "./TrainingVideoPlayerModal";

const CATEGORY_ICONS = {
  all: GraduationCapIcon,
  "employee-onboarding": UsersIcon,
  "patient-onboarding": HeartPulseIcon,
  billing: ReceiptIcon,
  libraries: LibraryIcon,
};

const Training = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState(TRAINING_CATEGORIES);
  const [videos, setVideos] = useState([]);
  const categoryIds = useMemo(
    () => [
      "all",
      ...new Set([
        ...TRAINING_CATEGORIES.map((c) => c.id),
        ...categories.map((c) => c.id),
      ]),
    ],
    [categories],
  );
  const [activeCategory, setActiveCategory] = useUrlTab(categoryIds, "all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [playerVideo, setPlayerVideo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await request("/training/videos");
      const payload = res.result || res;
      setCategories(
        payload.categories?.length ? payload.categories : TRAINING_CATEGORIES,
      );
      setVideos(payload.videos || []);
    } catch (err) {
      toast.error(err.message || "Failed to load training videos");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = useMemo(() => {
    const allCount = videos.length;
    return [
      {
        id: "all",
        label: allCount ? `All (${allCount})` : "All",
        icon: CATEGORY_ICONS.all,
      },
      ...categories.map((cat) => ({
        id: cat.id,
        label:
          typeof cat.count === "number"
            ? `${cat.label} (${cat.count})`
            : cat.label,
        icon: CATEGORY_ICONS[cat.id] || GraduationCapIcon,
      })),
    ];
  }, [categories, videos.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    let list = (videos || []).filter((v) => {
      if (activeCategory !== "all" && v.category !== activeCategory) {
        return false;
      }
      if (!q) return true;
      return (
        v.title?.toLowerCase().includes(q) ||
        v.shortTitle?.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q) ||
        v.categoryLabel?.toLowerCase().includes(q)
      );
    });

    list = [...list].sort((a, b) => {
      if (sortBy === "title") {
        return String(a.title || "").localeCompare(String(b.title || ""));
      }
      if (sortBy === "category") {
        const c = String(a.categoryLabel || "").localeCompare(
          String(b.categoryLabel || ""),
        );
        if (c !== 0) return c;
        return (a.sortOrder || 0) - (b.sortOrder || 0);
      }
      const ta = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const tb = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      if (sortBy === "oldest") return ta - tb;
      return tb - ta;
    });

    return list;
  }, [videos, activeCategory, search, sortBy]);

  const activeMeta =
    activeCategory === "all"
      ? {
          label: "All topics",
          description:
            "Staff orientation, patient charts, billing, and library walkthroughs.",
        }
      : categories.find((c) => c.id === activeCategory);

  return (
    <div
      className={`${PAGE_SHELL} lg:h-[calc(100dvh-3.5rem-2.5rem)] lg:min-h-0 lg:overflow-hidden lg:pb-0 flex flex-col`}
    >
      <div className={`${PAGE_HEADER_ROW} shrink-0`}>
        <div className="min-w-0">
          <h1 className={PAGE_TITLE}>Training</h1>
          <p className={PAGE_SUBTITLE}>
            Knowledge Center — short walkthroughs to help your team use the
            platform with confidence.
          </p>
        </div>
        <span
          className={`inline-flex items-center h-8 px-2.5 rounded-md text-xs font-semibold tabular-nums shrink-0 ${BRAND_THEME.section.badge}`}
        >
          {filtered.length} video{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      <ModuleTabPanel
        tabs={tabs}
        activeTab={activeCategory}
        onTabChange={setActiveCategory}
        theme={BRAND_THEME}
        className="flex-1 min-h-0 flex flex-col !overflow-hidden"
        contentClassName="flex-1 min-h-0 flex flex-col overflow-hidden bg-slate-50/50 p-0"
      >
        <div
          className={`shrink-0 flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 sm:px-4 py-2.5 border-b ${BRAND_THEME.section.head}`}
        >
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span
              className={`w-0.5 h-4 rounded-full shrink-0 ${BRAND_THEME.section.bar}`}
              aria-hidden
            />
            <div className="min-w-0">
              <p
                className={`text-xs font-semibold uppercase tracking-wide truncate ${BRAND_THEME.section.title}`}
              >
                {activeMeta?.label || "Training library"}
              </p>
              {activeMeta?.description ? (
                <p className="text-[11px] text-slate-500 truncate mt-0.5 hidden sm:block">
                  {activeMeta.description}
                </p>
              ) : null}
            </div>
          </div>

          <div className="relative w-full sm:w-[220px] lg:w-[260px] shrink-0">
            <SearchIcon
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search videos…"
              className={`${SEARCH_INPUT} ${BRAND_THEME.fieldFocus}`}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-slate-600 shrink-0">
            <span className="font-medium whitespace-nowrap text-slate-500">
              Sort
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`${FILTER_SELECT} ${BRAND_THEME.fieldFocus} min-w-[160px]`}
            >
              {TRAINING_SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 lg:p-5">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3.5 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-slate-200 bg-white overflow-hidden animate-pulse"
                >
                  <div className="aspect-[16/10] bg-gradient-to-br from-indigo-100 via-violet-100 to-indigo-200" />
                  <div className="p-3.5 space-y-2">
                    <div className="h-3.5 bg-slate-100 rounded w-5/6" />
                    <div className="h-3 bg-slate-50 rounded w-2/5" />
                    <div className="pt-2 flex items-center gap-2 border-t border-slate-100">
                      <div className="h-6 w-6 rounded-full bg-indigo-100" />
                      <div className="h-3 bg-slate-100 rounded flex-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              {videos.length === 0 ? (
                <>
                  <p className="text-sm font-semibold text-slate-700">
                    No training videos yet
                  </p>
                  <p className="mt-1 text-xs text-slate-500 max-w-sm">
                    Videos appear here once they are added for your agency.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-700">
                    No videos match your search
                  </p>
                  <p className="mt-1 text-xs text-slate-500 max-w-sm">
                    Clear the keyword or choose another topic tab.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3.5 sm:gap-4 pb-1">
              {filtered.map((video) => (
                <TrainingVideoCard
                  key={video.id || video.slug}
                  video={video}
                  onPlay={setPlayerVideo}
                />
              ))}
            </div>
          )}
        </div>
      </ModuleTabPanel>

      <TrainingVideoPlayerModal
        open={Boolean(playerVideo)}
        video={playerVideo}
        onClose={() => setPlayerVideo(null)}
      />
    </div>
  );
};

export default Training;
